import {
  AfterViewInit,
  Directive,
  ElementRef,
  OnDestroy,
  Renderer2,
  TemplateRef,
  inject,
  input
} from '@angular/core';
import { Placement } from '@floating-ui/dom';

import {
  DropdownHandle,
  DropdownMiddleware
} from '@shared/components/dropdown-singleton/dropdown-singleton.component';
import { DropdownService } from '@shared/components/dropdown-singleton/dropdown.service';

@Directive({
  selector: '[appDropdown]',
  standalone: true
})
export class DropdownDirective implements OnDestroy, AfterViewInit {
  appDropdown = input.required<TemplateRef<unknown>>();

  dropdownPosition = input<Placement>('bottom-start');
  dropdownMiddlewares = input<DropdownMiddleware[]>(['offset', 'flip', 'shift']);
  dropdownMaxWidth = input<number>(0);
  dropdownMinWidth = input<number>(0);
  dropdownMaxHeight = input<number>(0);
  dropdownMinHeight = input<number>(0);
  dropdownOffset = input<number>(8);
  dropdownGoUpperTarget = input<boolean>(false);
  dropdownCloseOnClickOutside = input<boolean>(true);
  dropdownCloseOnEscape = input<boolean>(true);

  dropdownContext = input<unknown>({});

  dropdownTrigger = input<('click' | 'hover' | 'dblclick' | 'enter')[]>(['click']);

  private dropdownService = inject(DropdownService);
  private renderer = inject(Renderer2);
  private elementRef = inject(ElementRef);
  private listeners: (() => void)[] = [];
  private isOpen = false;
  private handle?: DropdownHandle;

  ngAfterViewInit(): void {
    this.setupTriggers();
  }

  ngOnDestroy(): void {
    this.listeners.forEach((listener) => listener());
    this.close();
  }

  private setupTriggers(): void {
    const element = this.elementRef.nativeElement as HTMLElement;
    const trigger = this.dropdownTrigger();

    for (const t of trigger) {
      this.addTriggerListener(element, t);
    }
  }

  private addTriggerListener(
    element: HTMLElement,
    trigger: 'click' | 'hover' | 'dblclick' | 'enter'
  ): void {
    if (trigger === 'click') {
      const clickListener = this.renderer.listen(element, 'click', (event: Event) => {
        event.stopPropagation();
        this.toggle();
      });
      this.listeners.push(clickListener);
    } else if (trigger === 'dblclick') {
      const dblclickListener = this.renderer.listen(element, 'dblclick', (event: Event) => {
        event.stopPropagation();
        this.toggle();
      });
      this.listeners.push(dblclickListener);
    } else if (trigger === 'enter') {
      if (!element.hasAttribute('tabindex')) {
        element.setAttribute('tabindex', '0');
      }

      const keydownListener = this.renderer.listen(
        element,
        'keydown',
        (event: KeyboardEvent) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            event.stopPropagation();
            this.toggle();
          }
        }
      );
      this.listeners.push(keydownListener);
    } else if (trigger === 'hover') {
      const mouseEnterListener = this.renderer.listen(element, 'mouseenter', () => {
        this.open();
      });

      const mouseLeaveListener = this.renderer.listen(element, 'mouseleave', () => {
        this.close();
      });

      this.listeners.push(mouseEnterListener, mouseLeaveListener);
    }
  }

  private toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  private open(): void {
    this.isOpen = true;
    this.handle = this.dropdownService.show(
      this.elementRef.nativeElement,
      this.appDropdown(),
      {
        position: this.dropdownPosition(),
        middlewares: this.dropdownMiddlewares(),
        maxWidth: this.dropdownMaxWidth(),
        minWidth: this.dropdownMinWidth(),
        maxHeight: this.dropdownMaxHeight(),
        minHeight: this.dropdownMinHeight(),
        goUpperTarget: this.dropdownGoUpperTarget(),
        offset: this.dropdownOffset(),
        closeOnClickOutside: this.dropdownCloseOnClickOutside(),
        closeOnEscape: this.dropdownCloseOnEscape(),
        context: this.dropdownContext(),
        onClose: () => {
          this.isOpen = false;
          this.handle = undefined;
        }
      }
    );
  }

  private close(): void {
    this.isOpen = false;
    const handle = this.handle;
    this.handle = undefined;
    if (handle) {
      this.dropdownService.close(handle);
    }
  }
}
